import type { FC } from 'react'

import { Input, Radio, Label, InputWithValidation, RadioGroup, Tooltip, Icon } from '@skytech/ui'
import {
  getLocaleTimezoneIsoDate,
  getMaxDate,
  getMinDate,
  isOnlyDigits,
  isOnlyLetters,
} from '@skytech/utils'
import InfoVariantCircleOutlineIcon from 'mdi-react/InfoVariantCircleOutlineIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'

import { getIsActivationDateValid } from '../helpers'

import { useNewSubscriptionContext } from './NewSubscriptionForm.context'

import './NewSubscriptionDetails.styles.scss'

const NewSubscriptionDetails: FC = () => {
  const newSub = useNewSubscriptionContext()
  const translate = useTranslation()

  const [subType] = newSub.subType
  const [authorizationDocumentId] = newSub.authorizationDocumentId
  const [activationDate, setActivationDate] = newSub.activationDate
  const [simNumber, setSimNumber] = newSub.simNumber
  const [simAddress, setSimAddress] = newSub.simAddress
  const [simZip, setSimZip] = newSub.simZip
  const [simCity, setSimCity] = newSub.simCity
  const [simType, setSimType] = newSub.simType
  const [eid, setEid] = newSub.eid

  const isNewSimCardOptionAvailable =
    ['business', 'new-number'].includes(subType) || Boolean(authorizationDocumentId)

  const isPrivateNumber = subType === 'private'

  const [allowEsim] = newSub.allowEsim
  const [allowSubscriptions] = newSub.allowSubscriptions

  return (
    <div className="new-subscription-details">
      <h3>
        <Translation id="Subscription" />
      </h3>

      <div className="inputs">
        <div>
          <InputWithValidation
            isFullWidth
            id="activation-date"
            label={<Translation id="Start date" />}
            type="date"
            value={activationDate}
            min={getLocaleTimezoneIsoDate(getMinDate(3))}
            max={getLocaleTimezoneIsoDate(getMaxDate(12))}
            validator={() => getIsActivationDateValid(activationDate)}
            errorMessage={
              <Translation
                id={
                  activationDate
                    ? 'The provided date must fall between 3 months prior to today and 1 year after today'
                    : 'This field is mandatory'
                }
              />
            }
            onChange={e => setActivationDate(e.target.value)}
          />
        </div>

        {!isPrivateNumber && allowSubscriptions && (
          <RadioGroup
            id="sim-type"
            className="is-new-sim mb-2 flex-col gap-1"
            label={<Translation id="Need a sim card?" />}
          >
            <Radio
              id="is-existing-sim"
              checked={simType === 'existing'}
              onChange={() => {
                setSimType('existing')
              }}
              onClick={() => {
                // To allow deselecting
                if (simType === 'existing') setSimType(undefined)
              }}
              label={<Translation id="I have a SIM card" />}
            />

            {isNewSimCardOptionAvailable && (
              <Radio
                id="is-new-sim"
                checked={simType === 'new'}
                label={<Translation id="I need a new SIM card" />}
                onChange={() => {
                  setSimType('new')
                }}
                onClick={() => {
                  // To allow deselecting
                  if (simType === 'new') setSimType(undefined)
                }}
              />
            )}

            {allowEsim && (
              <Radio
                id="is-e-sim"
                checked={simType === 'eSim'}
                onChange={() => {
                  setSimType('eSim')
                }}
                onClick={() => {
                  // To allow deselecting
                  if (simType === 'eSim') setSimType(undefined)
                }}
                label={<Translation id="I want to activate eSim" />}
              />
            )}
          </RadioGroup>
        )}

        <div className={twJoin('relative', !simType && 'opacity-0')}>
          {simType !== undefined && (
            <>
              {simType === 'new' && (
                <>
                  <Label>
                    <Translation id="Where should the SIM card be sent?" />
                  </Label>

                  <Input
                    id="new-sub-address"
                    isFullWidth
                    marginBottom={16}
                    label={<Translation id="Address" className="capitalize" />}
                    value={simAddress}
                    onChange={e => setSimAddress(e.target.value)}
                  />

                  <div className="flex">
                    <Input
                      isFullWidth
                      marginRight={16}
                      wrapperClassName="shrink-[3]"
                      id="zip"
                      label={<Translation id="Zip" />}
                      maxLength={10}
                      value={simZip || ''}
                      onChange={e => setSimZip(e.target.value)}
                    />

                    <InputWithValidation
                      isFullWidth
                      id="city"
                      label={<Translation id="City" />}
                      value={simCity || ''}
                      errorMessage={<Translation id="City name may only consist of letters" />}
                      validator={isOnlyLetters}
                      onChange={e => setSimCity(e.target.value)}
                    />
                  </div>
                </>
              )}

              {simType === 'existing' && (
                <InputWithValidation
                  id="sim-card-number"
                  isFullWidth
                  label={
                    <>
                      <Translation id="Sim card number" /> ({simNumber?.length || 0}/20)
                    </>
                  }
                  value={simNumber || ''}
                  validator={val => isOnlyDigits(val) && isStringOfLength(String(val), 20)}
                  errorMessage={<Translation id="Sim card number must consist of 20 digits" />}
                  onChange={e => setSimNumber(e.target.value)}
                  maxLength={20}
                />
              )}

              {simType === 'eSim' && (
                <InputWithValidation
                  id="eid"
                  isFullWidth
                  label={
                    <>
                      <Translation id="EID number" /> ({eid?.length || 0}/32)
                    </>
                  }
                  value={eid || ''}
                  validator={val => isOnlyDigits(val) && isStringOfLength(String(val), 32)}
                  errorMessage={<Translation id="EID number must consist of 32 digits" />}
                  onChange={e => setEid(e.target.value)}
                  maxLength={32}
                />
              )}

              <Tooltip id="eid-info" size="large" />

              {simType === 'eSim' && (
                <Icon
                  colorMode="info"
                  isWhiteLabeled={false}
                  className="absolute right-3 top-3"
                  data-tooltip-id="eid-info"
                  data-tooltip-content={translate('eid-tooltip-info')}
                >
                  <InfoVariantCircleOutlineIcon />
                </Icon>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default NewSubscriptionDetails

function isStringOfLength(val: string, length: number) {
  return val.length === length
}
